import { Box, Button, Fade, IconButton, Popper } from '@mui/material';
import { styled } from '@mui/material/styles';
import theme from 'theme/theme';
import CloseIcon from '@mui/icons-material/Close';
import { createPortal } from 'react-dom';
import type { BaseTourStop } from './tourContextBase';
import { memo, useState, useEffect } from 'react';

const StyledPopper = styled(Popper)`
  && {
    z-index: 10000;
    padding: 0;
    background: white;
    border: 1px solid ${theme.palette.grey[300]};
    max-width: 300px;
    border-radius: 8px;
  }
  box-shadow: 0px 3px 5px -1px rgba(0, 0, 0, 0.1);
  filter: drop-shadow(0px 6px 10px rgba(0, 0, 0, 0.07))
    drop-shadow(0px 1px 18px rgba(0, 0, 0, 0.06));
`;

const Arrow = styled('span')`
  position: absolute;
  font-size: 7px;
  width: 3em;
  height: 3em;
  z-index: 1;
  background: white;
  border: 1px solid ${theme.palette.grey[300]};

  &[data-placement='top'] {
    right: 50%;
    transform: translate(50%, -50%) rotate(45deg);
  }

  &[data-placement='right-start'] {
    left: -0.65rem;
    top: 1.65rem;
    transform: translateY(-50%) rotate(45deg);
  }

  &[data-placement='left-start'] {
    right: -0.65rem;
    top: 1.65rem;
    transform: translateY(-50%) rotate(45deg);
  }

  &[data-placement='left'] {
    right: -0.65rem;
    top: 50%;
    transform: translateY(-50%) rotate(45deg);
  }

  &[data-placement='right-end'] {
    left: -0.65rem;
    bottom: 0;
    transform: translateY(-50%) rotate(45deg);
  }
`;

const PopperContent = styled('div')`
  position: relative;
  background: white;
  z-index: 2;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
`;

const Header = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-sizing: border-box;
  color: ${theme.palette.grey[900]};
  padding: ${theme.spacing(1)} ${theme.spacing(2)};
`;

const Title = styled('span')`
  font-weight: 700;
  font-size: 14px;
  line-height: 143%;
`;

const Text = styled('span')`
  padding: ${theme.spacing(2)};
  font-weight: 400;
  font-size: 13px;
  line-height: 146%;
`;

const BottomRow = styled('div')`
  display: flex;
  box-sizing: border-box;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding-right: ${theme.spacing(2)};
  padding-left: ${theme.spacing(2)};
  padding-bottom: ${theme.spacing(2)};
`;

const Counter = styled('span')`
  font-weight: 500;
  font-size: 12px;
`;

const ButtonGroup = styled('div')`
  display: flex;
  > * {
    margin-left: ${theme.spacing(1)};
  }
`;

const Image = styled('img')`
  border-bottom: 1px solid ${theme.palette.grey[300]};
  border-top: 1px solid ${theme.palette.grey[300]};
`;

/**
 * Returns popover text content with emphasized words
 * in bold.
 */
const TextContent = ({
  text,
  emphasize,
}: {
  text: string;
  emphasize: string[];
}) => {
  if (emphasize.length === 0) {
    return <Text>{text}</Text>;
  }

  const result: React.ReactElement[] = [];
  const remainder = emphasize.reduce((acc: string, bold: string, i) => {
    const t = acc.split(bold);
    result.push(
      <span key={`${t[0]}${i}`}>{t[0]}</span>,
      <strong key={`${bold}${i}-bold`}>{bold}</strong>,
    );
    return acc.replace(t[0], '').replace(bold, '');
  }, text);
  result.push(<span key='remainder'>{remainder}</span>);

  return <Text>{result.map((r) => r)}</Text>;
};

export interface TourPopoverBaseProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  currentStop: BaseTourStop;
  isFirstStop: boolean;
  isLastStop: boolean;
  currentStopNumber: number;
  totalStops: number;
  goToNextStop: () => void;
  goToPreviousStop: () => void;
  skipTour: () => void;
  disableAutoRepositioning?: boolean;
  useFixedStrategy?: boolean;
}

const TourPopoverBaseInner = ({
  open,
  anchorEl,
  currentStop,
  isFirstStop,
  isLastStop,
  currentStopNumber,
  totalStops,
  goToNextStop,
  goToPreviousStop,
  skipTour,
  disableAutoRepositioning = false,
  useFixedStrategy = false,
}: TourPopoverBaseProps) => {
  const [isPositioned, setIsPositioned] = useState(false);

  useEffect(() => {
    setIsPositioned(false);
    const timer = setTimeout(() => {
      setIsPositioned(true);
    }, 50);
    return () => clearTimeout(timer);
  }, [currentStop.id]);

  const popoverContent = (
    <>
      <Arrow data-placement={currentStop.placement} />
      <PopperContent>
        <Header>
          <Title>{currentStop.title}</Title>
          <Button size='small' onClick={skipTour}>
            Skip
          </Button>
          <IconButton
            aria-label='Exit walkthrough'
            size='small'
            onClick={skipTour}
          >
            <CloseIcon fontSize='small' />
          </IconButton>
        </Header>

        {currentStop.image && <Image src={currentStop.image} />}
        <TextContent
          text={currentStop.text}
          emphasize={currentStop.emphasize}
        />
        <BottomRow>
          <Counter>{`${currentStopNumber} of ${totalStops}`}</Counter>
          <ButtonGroup>
            {!isFirstStop && (
              <Button
                variant='outlined'
                color='tertiary'
                onClick={goToPreviousStop}
              >
                Back
              </Button>
            )}
            <Button
              variant='contained'
              sx={{ boxShadow: 'none', marginLeft: theme.spacing(1) }}
              onClick={goToNextStop}
            >
              {isLastStop ? 'Got it!' : 'Next'}
            </Button>
          </ButtonGroup>
        </BottomRow>
      </PopperContent>
    </>
  );

  if (currentStop.position) {
    return createPortal(
      <Fade in={open && isPositioned} timeout={400}>
        <Box
          sx={{
            position: 'fixed',
            zIndex: 99999,
            maxWidth: '300px',
            background: 'white',
            border: `1px solid ${theme.palette.grey[300]}`,
            borderRadius: '8px',
            boxShadow: '0px 3px 5px -1px rgba(0, 0, 0, 0.1)',
            filter:
              'drop-shadow(0px 6px 10px rgba(0, 0, 0, 0.07)) drop-shadow(0px 1px 18px rgba(0, 0, 0, 0.06))',
            ...currentStop.position,
          }}
        >
          {popoverContent}
        </Box>
      </Fade>,
      document.body,
    );
  }

  if (!anchorEl) {
    return (
      <Fade in={open && isPositioned} timeout={400}>
        <Box
          sx={{
            maxWidth: '300px',
            background: 'white',
            border: `1px solid ${theme.palette.grey[300]}`,
            borderRadius: '8px',
            boxShadow: '0px 3px 5px -1px rgba(0, 0, 0, 0.1)',
            filter:
              'drop-shadow(0px 6px 10px rgba(0, 0, 0, 0.07)) drop-shadow(0px 1px 18px rgba(0, 0, 0, 0.06))',
          }}
        >
          {popoverContent}
        </Box>
      </Fade>
    );
  }

  return (
    <StyledPopper
      id={currentStop.id}
      open={open && isPositioned}
      anchorEl={anchorEl}
      transition
      placement={currentStop.placement}
      keepMounted={disableAutoRepositioning}
      modifiers={[
        {
          name: 'offset',
          options: {
            offset: [0, 12],
          },
        },
        {
          name: 'preventOverflow',
          enabled: !disableAutoRepositioning,
        },
        {
          name: 'flip',
          enabled: !disableAutoRepositioning,
        },
        {
          name: 'eventListeners',
          enabled: !disableAutoRepositioning,
        },
      ]}
      popperOptions={{
        strategy: useFixedStrategy ? 'fixed' : 'absolute',
      }}
    >
      {({
        TransitionProps,
      }: {
        TransitionProps?: {
          in: boolean;
          onEnter: () => void;
          onExited: () => void;
        };
      }) => (
        <Fade {...TransitionProps} timeout={400}>
          <Box sx={{ maxWidth: '300px' }}>{popoverContent}</Box>
        </Fade>
      )}
    </StyledPopper>
  );
};

const TourPopoverBase = memo(TourPopoverBaseInner);

export default TourPopoverBase;
